import React, { useMemo, useState } from 'react';
import {
  Alert,
  Button,
  Card,
  Col,
  Form,
  Modal,
  Row,
  Spinner
} from 'react-bootstrap';
import {
  CardElement,
  Elements,
  useElements,
  useStripe
} from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import {
  faCheckCircle,
  faCreditCard,
  faExclamationCircle,
  faExclamationTriangle,
  faExchangeAlt,
  faPlusCircle,
  faShieldAlt,
  faSyncAlt,
  faTimes,
  faTrashAlt
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Flex from 'components/common/Flex';
import moment from 'moment';
import { confirmAlert } from 'react-confirm-alert';
import { toast } from 'react-toastify';
import PropTypes from 'prop-types';
import {
  useConfirmOrganizationPaymentMethodMutation,
  useCreateOrganizationSetupIntentMutation,
  useDetachOrganizationPaymentMethodMutation,
  useGetOrganizationBillingProfileQuery,
  useGetOrganizationPaymentMethodsQuery,
  useSetOrganizationPaymentMethodRoleMutation
} from 'rtk/org-admin/organizationBilling.slice';

const dataOf = value => value?.Data ?? value?.data ?? value;
const field = (value, name) =>
  value?.[name] ?? value?.[name[0].toLowerCase() + name.slice(1)];
const requestKey = () =>
  window.crypto?.randomUUID?.() ??
  `${Date.now()}-${Math.random().toString(16).slice(2)}`;
const errorText = error => {
  // The unwrapped RTK error is the API body itself; the specific reason is in Data, the generic code text in Message.
  const value =
    error?.data?.Data ??
    error?.data?.data ??
    error?.Data ??
    error?.data?.Message ??
    error?.Message ??
    error?.message ??
    error?.error;
  return typeof value === 'string' && value.trim()
    ? value
    : 'The request could not be completed.';
};
const isCardExpired = (expMonth, expYear) => {
  if (!expMonth || !expYear) return false;
  const cardExpiry = moment(`${expYear}-${expMonth}`, 'YYYY-M').endOf('month');
  return moment().isAfter(cardExpiry);
};
const CardSetupForm = ({ setup, onConfirmed }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [saving, setSaving] = useState(false);

  const submit = async event => {
    event.preventDefault();
    if (!stripe || !elements) return;
    setSaving(true);
    const result = await stripe.confirmCardSetup(field(setup, 'ClientSecret'), {
      payment_method: { card: elements.getElement(CardElement) }
    });
    if (result.error) {
      toast.error(result.error.message);
      setSaving(false);
      return;
    }
    try {
      await onConfirmed(result.setupIntent.id);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Form onSubmit={submit} autoComplete="off">
      <CardElement />
      <Flex className="my-3" justifyContent="center">
        <Button
          type="submit"
          disabled={!stripe || !elements || saving}
          className="mt-3"
          variant="primary"
        >
          {saving ? 'Saving...' : 'Add Card'}
        </Button>
      </Flex>
    </Form>
  );
};

CardSetupForm.propTypes = {
  setup: PropTypes.object.isRequired,
  onConfirmed: PropTypes.func.isRequired
};

const AdminSubscription = () => {
  const profileQuery = useGetOrganizationBillingProfileQuery();
  const cardsQuery = useGetOrganizationPaymentMethodsQuery();
  const [createSetup, setupState] = useCreateOrganizationSetupIntentMutation();
  const [confirmCard] = useConfirmOrganizationPaymentMethodMutation();
  const [setRole] = useSetOrganizationPaymentMethodRoleMutation();
  const [detach] = useDetachOrganizationPaymentMethodMutation();
  const [settingPrimaryGuid, setSettingPrimaryGuid] = useState(null);
  const [settingBackupGuid, setSettingBackupGuid] = useState(null);
  const [removingRoleGuid, setRemovingRoleGuid] = useState(null);
  const [swappingCards, setSwappingCards] = useState(false);
  const [removingGuid, setRemovingGuid] = useState(null);
  const cards = dataOf(cardsQuery.data) || [];
  const [setup, setSetup] = useState(null);

  const stripePromise = useMemo(() => {
    const key = setup && field(setup, 'PublishableKey');
    return key ? loadStripe(key) : null;
  }, [setup]);

  const beginAddCard = async () => {
    try {
      const response = await createSetup(requestKey()).unwrap();
      const setupData = dataOf(response);
      setSetup(setupData);
      const notice = field(setupData, 'Notice');
      if (notice) {
        toast.warning(notice);
        cardsQuery.refetch();
      }
    } catch (error) {
      toast.error(errorText(error));
    }
  };

  const finishAddCard = async setupIntentId => {
    try {
      await confirmCard(setupIntentId).unwrap();
      setSetup(null);
      toast.success('Payment method saved.');
    } catch (error) {
      toast.error(errorText(error));
      throw error;
    }
  };

  const changeRole = async (guid, isDefault, isBackup) => {
    if (isDefault) setSettingPrimaryGuid(guid);
    else if (isBackup) setSettingBackupGuid(guid);
    else setRemovingRoleGuid(guid);
    try {
      await setRole({
        guid,
        idempotencyKey: requestKey(),
        isDefault,
        isBackup
      }).unwrap();
      toast.success(
        isDefault
          ? 'Card set as primary successfully'
          : isBackup
          ? 'Card set as backup successfully'
          : 'Card role updated.'
      );
    } catch (error) {
      toast.error(errorText(error));
    } finally {
      setSettingPrimaryGuid(null);
      setSettingBackupGuid(null);
      setRemovingRoleGuid(null);
    }
  };

  const swapPrimaryBackup = async () => {
    const primary = cards.find(card => field(card, 'IsDefault'));
    const backup = cards.find(card => field(card, 'IsBackup'));
    if (!primary || !backup) return;
    setSwappingCards(true);
    try {
      await setRole({
        guid: field(primary, 'GUID'),
        idempotencyKey: requestKey(),
        isDefault: false,
        isBackup: true
      }).unwrap();
      await setRole({
        guid: field(backup, 'GUID'),
        idempotencyKey: requestKey(),
        isDefault: true,
        isBackup: false
      }).unwrap();
      toast.success('Primary and Backup cards swapped');
    } catch (error) {
      toast.error(errorText(error));
    } finally {
      setSwappingCards(false);
    }
  };

  const removeCard = guid => {
    confirmAlert({
      message: 'Are you sure you want to delete this payment method?',
      buttons: [
        {
          label: 'Yes',
          onClick: () => proceedToRemoveCard(guid)
        },
        {
          label: 'No',
          onClick: () => {}
        }
      ]
    });
  };

  const proceedToRemoveCard = async guid => {
    setRemovingGuid(guid);
    try {
      await detach({ guid, idempotencyKey: requestKey() }).unwrap();
      toast.success('Payment method removed successfully.');
    } catch (error) {
      toast.error(errorText(error));
    } finally {
      setRemovingGuid(null);
    }
  };

  if (profileQuery.isLoading || cardsQuery.isLoading) {
    return (
      <Spinner
        animation="border"
        role="status"
        aria-label="Loading organization billing"
      />
    );
  }

  const loadError = profileQuery.error || cardsQuery.error;
  return (
    <Row className="g-3 mb-3">
      <Col xs={12}>
        <h2 className="mb-0">Organization billing</h2>
        <p className="text-600 mb-0">
          Manage the organization payment methods.
        </p>
      </Col>
      {loadError && (
        <Col xs={12}>
          <Alert variant="danger">{errorText(loadError)}</Alert>
        </Col>
      )}
      <Col xs={12}>
        <Card>
          <Card.Header>
            <Flex justifyContent="between" alignItems="center">
              <h5 className="mb-0">Added payment cards</h5>
              <Button
                onClick={beginAddCard}
                variant="primary"
                disabled={setupState.isLoading || !!setup}
              >
                {setupState.isLoading ? (
                  'Preparing…'
                ) : (
                  <>
                    <FontAwesomeIcon icon={faPlusCircle} size="sm" />{' '}
                    <span>Add</span>
                  </>
                )}
              </Button>
            </Flex>
          </Card.Header>
          <Card.Body className="py-0">
            {!cards.length && !setup && (
              <Col sm={12} className="text-center text-muted my-3">
                <p>
                  <FontAwesomeIcon icon={faCreditCard} size="2x" />
                </p>
                <p>You have not added payment method yet.</p>
              </Col>
            )}
            {cards.some(card => field(card, 'IsDefault')) &&
              cards.some(card => field(card, 'IsBackup')) && (
                <div className="my-3">
                  <Button
                    variant="outline-primary"
                    size="sm"
                    onClick={swapPrimaryBackup}
                    disabled={swappingCards}
                  >
                    {swappingCards ? (
                      <>
                        <Spinner size="sm" className="me-1" />
                        Swapping...
                      </>
                    ) : (
                      <>
                        <FontAwesomeIcon
                          icon={faExchangeAlt}
                          className="me-1"
                        />
                        Swap Primary & Backup
                      </>
                    )}
                  </Button>
                </div>
              )}
            <Row>
              {cards.map(card => {
                const guid = field(card, 'GUID');
                const isPrimary = !!field(card, 'IsDefault');
                const isBackup = !!field(card, 'IsBackup');
                const expMonth = field(card, 'ExpMonth');
                const expYear = field(card, 'ExpYear');
                const isExpired = isCardExpired(expMonth, expYear);
                const created = field(card, 'CreatedUtc');
                const settingPrimary = settingPrimaryGuid === guid;
                const settingBackup = settingBackupGuid === guid;
                const removingRole = removingRoleGuid === guid;
                const removingThis = removingGuid === guid;
                return (
                  <Col key={guid} sm={12} md={6} lg={6} xl={4} className="my-3">
                    <Card
                      bg={isExpired ? 'soft-primary' : 'soft-success'}
                      text="dark"
                      className={isExpired ? 'border-primary' : ''}
                    >
                      <Card.Body>
                        <Card.Title as="div">
                          <Flex justifyContent="between" className="fs--1">
                            <div className="d-flex gap-1">
                              {!isPrimary && !isBackup && (
                                <>
                                  <Button
                                    size="sm"
                                    variant="outline-primary"
                                    disabled={isExpired || settingPrimary}
                                    title={
                                      isExpired
                                        ? "You can't set a primary as card has expired."
                                        : undefined
                                    }
                                    onClick={() =>
                                      changeRole(guid, true, false)
                                    }
                                  >
                                    {settingPrimary
                                      ? 'Setting...'
                                      : 'Set Primary'}
                                  </Button>
                                  <Button
                                    size="sm"
                                    variant="outline-secondary"
                                    disabled={isExpired || settingBackup}
                                    title={
                                      isExpired
                                        ? "You can't set a backup as card has expired."
                                        : undefined
                                    }
                                    onClick={() =>
                                      changeRole(guid, false, true)
                                    }
                                  >
                                    {settingBackup
                                      ? 'Setting...'
                                      : 'Set Backup'}
                                  </Button>
                                </>
                              )}
                              {(isPrimary || isBackup) && (
                                <Button
                                  size="sm"
                                  variant="outline-danger"
                                  disabled={removingRole}
                                  onClick={() => changeRole(guid, false, false)}
                                >
                                  {removingRole ? (
                                    'Removing...'
                                  ) : (
                                    <>
                                      <FontAwesomeIcon
                                        icon={faTimes}
                                        className="me-1"
                                      />
                                      Remove from{' '}
                                      {isPrimary ? 'Primary' : 'Backup'}
                                    </>
                                  )}
                                </Button>
                              )}
                            </div>
                            <div className="d-flex align-items-center gap-2">
                              {isPrimary && (
                                <span className="p-2 badge bg-primary">
                                  <FontAwesomeIcon
                                    icon={faShieldAlt}
                                    className="me-1"
                                  />
                                  Primary
                                </span>
                              )}
                              {isBackup && (
                                <span className="p-2 badge bg-secondary">
                                  <FontAwesomeIcon
                                    icon={faSyncAlt}
                                    className="me-1"
                                  />
                                  Backup
                                </span>
                              )}
                              {isExpired && (
                                <span className="p-2 badge bg-warning text-white">
                                  <FontAwesomeIcon
                                    icon={faExclamationTriangle}
                                    className="me-1 text-white"
                                  />
                                  Expired
                                </span>
                              )}
                            </div>
                          </Flex>
                        </Card.Title>
                        <Flex justifyContent="between" className="fs--1">
                          <div>
                            <span className="fs-0">xxxx xxxx xxxx</span>{' '}
                            <span className="fs-2">{field(card, 'Last4')}</span>
                          </div>
                          <Card.Text className="text-end text-capitalize">
                            {field(card, 'CardType')}
                          </Card.Text>
                        </Flex>
                        <Flex justifyContent="between" className="fs--1">
                          <Card.Text>
                            {isExpired ? (
                              <div className="text-warning">
                                <FontAwesomeIcon icon={faExclamationCircle} />{' '}
                                Expired
                              </div>
                            ) : (
                              <div>
                                <FontAwesomeIcon icon={faCheckCircle} /> Active
                              </div>
                            )}
                          </Card.Text>
                        </Flex>
                        <Flex justifyContent="between" className="fs--1">
                          <Card.Text>
                            {expMonth} / {expYear}
                          </Card.Text>
                        </Flex>
                        <Flex justifyContent="between" className="fs--1 mt-3">
                          <Card.Text className="mb-0 mt-1">
                            {removingThis ? (
                              <Spinner size="sm" />
                            ) : (
                              <FontAwesomeIcon
                                icon={faTrashAlt}
                                className="cursor-pointer"
                                onClick={() => removeCard(guid)}
                              />
                            )}
                          </Card.Text>
                          <Card.Text className="text-end mb-0">
                            {created ? (
                              <>
                                Added on{' '}
                                <span className="fs-1">
                                  {moment(created).local().format('MM/DD/YYYY')}
                                </span>
                              </>
                            ) : null}
                          </Card.Text>
                        </Flex>
                      </Card.Body>
                    </Card>
                  </Col>
                );
              })}
            </Row>
            {setup && stripePromise && (
              <Modal
                show
                onHide={() => setSetup(null)}
                size="md"
                aria-labelledby="organization-add-card-title"
                centered
                backdrop="static"
              >
                <Modal.Header closeButton>
                  <Modal.Title id="organization-add-card-title">
                    Add Card
                  </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                  <Row>
                    <Col sm={12}>
                      <Elements stripe={stripePromise}>
                        <CardSetupForm
                          setup={setup}
                          onConfirmed={finishAddCard}
                        />
                      </Elements>
                    </Col>
                  </Row>
                </Modal.Body>
              </Modal>
            )}
          </Card.Body>
        </Card>
      </Col>
    </Row>
  );
};

export default AdminSubscription;
